import mapboxgl from 'mapbox-gl';
mapboxgl.accessToken = mapboxConfig.accessToken;

// ==================== CONSTANTES ====================
const MAP_CONFIG = {
    center: [5.92085, 45.5667],
    zoom: 7,
    minZoom: 6,
    maxZoom: 15,
    fitBoundsPadding: 60,
    fitBoundsDuration: 800,
};

const CLUSTER_CONFIG = {
    maxZoom: 14,
    radius: 50,
    sizes: { small: 18, medium: 22, large: 28 },
    thresholds: { medium: 10, large: 30 },
};

const LAYER_IDS = {
    clusters: 'clusters',
    clusterCount: 'cluster-count',
    points: 'poi-layer',
};

// ==================== HELPERS ====================
function buildPOIUrl(pole, lang) {
    const base = `${rest_url}wp/v2/map/`;
    return pole ? `${base}${pole}/?lang=${lang}` : `${base}?lang=${lang}`;
}

function getPopupConfig(lang, inCluster, clusterPathFr, clusterPathEn, pole) {
    const isEnglish = lang === 'en';
    const isInCluster = inCluster === 'true';

    if (isInCluster) {
        return {
            url: '#worksList',
            btnText: isEnglish ? 'See the clusters ⇣' : 'Voir les ouvrages ⇣',
        };
    }

    return {
        url: isEnglish ? `/en/${clusterPathEn}/${pole}` : `/${clusterPathFr}/${pole}`,
        btnText: isEnglish ? 'See the cluster ⤑' : 'Voir le pôle ⤑',
    };
}

function createPopupHTML(title, poleString, poleType, url, btnText) {
    return `
        <div class="map-popup">
            <div class="map-popup__header">
                <p>${poleString}</p>
                <p>${poleType}</p>
            </div>
            <div class="map-popup__title">${title}</div>
            <a href="${url}" class="map-popup__link">${btnText}</a>
        </div>
    `;
}

// ==================== LAYERS ====================
function addClusterLayers(map) {
    // Clusters circles
    map.addLayer({
        id: LAYER_IDS.clusters,
        type: 'circle',
        source: 'poi',
        filter: ['has', 'point_count'],
        paint: {
            'circle-stroke-width': 5,
            'circle-stroke-color': '#fff',
            'circle-stroke-opacity': 0.6,
            'circle-color': '#fff',
            'circle-radius': [
                'step',
                ['get', 'point_count'],
                CLUSTER_CONFIG.sizes.small,
                CLUSTER_CONFIG.thresholds.medium, CLUSTER_CONFIG.sizes.medium,
                CLUSTER_CONFIG.thresholds.large, CLUSTER_CONFIG.sizes.large,
            ],
        },
    });

    // Cluster count labels
    map.addLayer({
        id: LAYER_IDS.clusterCount,
        type: 'symbol',
        source: 'poi',
        filter: ['has', 'point_count'],
        layout: {
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Roboto', 'Arial Unicode MS Bold'],
            'text-size': 14,
        },
        paint: {
            'text-color': '#000',
        },
    });

    // Individual points
    map.addLayer({
        id: LAYER_IDS.points,
        type: 'circle',
        source: 'poi',
        filter: ['!', ['has', 'point_count']],
        paint: {
            'circle-radius': 8,
            'circle-color': '#fff',
            'circle-stroke-width': 5,
            'circle-stroke-color': '#fff',
            'circle-stroke-opacity': 0.6,
        },
    });
}

function fitMapToBounds(map, features) {
    if (!features?.length) return;

    const bounds = features.reduce(
        (bounds, feature) => bounds.extend(feature.geometry.coordinates),
        new mapboxgl.LngLatBounds()
    );

    map.fitBounds(bounds, {
        padding: MAP_CONFIG.fitBoundsPadding,
        maxZoom: MAP_CONFIG.maxZoom,
        duration: MAP_CONFIG.fitBoundsDuration,
    });
}

// ==================== EVENT HANDLERS ====================
function setupEventHandlers(map, popupConfig) {
    // Cluster click - expand
    map.on('click', LAYER_IDS.clusters, (e) => {
        const features = map.queryRenderedFeatures(e.point, {
            layers: [LAYER_IDS.clusters],
        });

        if (!features.length) return;

        const clusterId = features[0].properties.cluster_id;
        map.getSource('poi').getClusterExpansionZoom(clusterId, (err, zoom) => {
            if (err) return;

            map.easeTo({
                center: features[0].geometry.coordinates,
                zoom,
            });
        });
    });

    // Point click - show popup
    map.on('click', LAYER_IDS.points, (e) => {
        const feature = e.features?.[0];
        if (!feature) return;

        const { coordinates } = feature.geometry;
        const { title, poleString, poleType, pole } = feature.properties;

        map.easeTo({
            center: coordinates,
            duration: 500,
        });

        const { url, btnText } = getPopupConfig(
            popupConfig.lang,
            popupConfig.inCluster,
            popupConfig.clusterPathFr,
            popupConfig.clusterPathEn,
            pole
        );

        new mapboxgl.Popup()
            .setLngLat(coordinates)
            .setHTML(createPopupHTML(title, poleString, poleType, url, btnText))
            .addTo(map);
    });

    // Cursor UX
    const cursorLayers = [LAYER_IDS.clusters, LAYER_IDS.points];

    cursorLayers.forEach(layer => {
        map.on('mouseenter', layer, () => {
            map.getCanvas().style.cursor = 'pointer';
        });
        map.on('mouseleave', layer, () => {
            map.getCanvas().style.cursor = '';
        });
    });
}

// ==================== MAIN INIT ====================
async function initMap() {
    const mapEl = document.getElementById('map');
    if (!mapEl) return;

    const { pole, lang, path_fr, path_en, in_cluster } = mapEl.dataset;

    const POI_URL = buildPOIUrl(pole, lang);

    const map = new mapboxgl.Map({
        container: mapEl,
        center: MAP_CONFIG.center,
        zoom: MAP_CONFIG.zoom,
        minZoom: MAP_CONFIG.minZoom,
        style: 'mapbox://styles/mapbox/standard-satellite',
        config: {
            basemap: {
                showPedestrianRoads: false,
                showPointOfInterestLabels: false,
                showRoadLabels: false,
                showTransitLabels: false,
                showAdminBoundaries: false,
                showRoadsAndTransit: false,
            },
        },
    });

    map.on('load', async () => {
        try {
            const response = await fetch(POI_URL);

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const geojson = await response.json();

            // Add source with clustering
            map.addSource('poi', {
                type: 'geojson',
                data: geojson,
                cluster: true,
                clusterMaxZoom: CLUSTER_CONFIG.maxZoom,
                clusterRadius: CLUSTER_CONFIG.radius,
            });

            // Add all layers
            addClusterLayers(map);

            // Fit bounds to show all points
            fitMapToBounds(map, geojson.features);

            // Setup interactions
            setupEventHandlers(map, {
                lang,
                inCluster: in_cluster,
                clusterPathFr: path_fr,
                clusterPathEn: path_en,
            });

        } catch (error) {
            console.error('Erreur lors du chargement des POI:', error);
        }
    });
}

// ==================== AUTO-INIT ====================
if (
    document.body.classList.contains('home') ||
    document.body.classList.contains('page-template-hub')
) {
    initMap();
}